import { ImagePlus } from 'tiptap-image-plus';
import { mergeAttributes } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

interface CustomImageOptions {
  HTMLAttributes: any;
  allowBase64: boolean;
  lastValidWidth: string;
  notifyImageInsertionError: () => {};
}

export const CustomImage = ImagePlus.extend<CustomImageOptions>({
  name: 'customImage',

  addAttributes() {
    return {
      ...this.parent?.(),

      width: {
        default: null,
        parseHTML: element => element.getAttribute('data-width') || element.style.width || null,
        renderHTML: attributes => {
          if (!attributes.width) return {};

          if (typeof attributes.width != 'string' || !attributes.width.includes('%')) {
            attributes.width = this.options.lastValidWidth;
          }
          this.options.lastValidWidth = attributes.width;

          return {
            'data-width': attributes.width,
            style: `width: ${attributes.width};`
          };
        }
      },

      align: {
        default: null,
        parseHTML: element => element.getAttribute('data-align') || element.style.float || null,
        renderHTML: attributes => {
          if (!attributes.align) return {};
          return {
            'data-align': attributes.align,
            style: `float: ${attributes.align};`
          };
        }
      },

      'data-image-id': {
        parseHTML: element => element.getAttribute('data-image-id') || getRandomId(),
        renderHTML: attributes => {
          if (!attributes['data-image-id']) return {};
          return {
            'data-image-id': attributes['data-image-id']
          };
        }
      }
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addProseMirrorPlugins() {
    const processedNodes = new Set<string>();
    const pendingNodes = new Set<string>();

    return [
      new Plugin({
        appendTransaction: (transactions, oldState, newState) => {
          const doc = newState.doc;

          doc.descendants((node, pos) => {
            if (node.type.name === 'customImage' && node.attrs.src) {
              const src = node.attrs.src;
              const nodeId = node.attrs['data-image-id'] || getRandomId();

              if (processedNodes.has(nodeId) || pendingNodes.has(nodeId)) return true;

              pendingNodes.add(nodeId);

              setTimeout(async () => {
                const view = this.editor?.view;
                if (!view) return;

                try {
                  const { base64, width } = await loadImage(src);

                  processedNodes.add(nodeId);
                  pendingNodes.delete(nodeId);

                  const tr = view.state.tr;
                  let changed = false;

                  view.state.doc.descendants((n, p) => {
                    if (n.type.name === 'customImage' && n.attrs.src === src) {
                      tr.setNodeMarkup(p, undefined, {
                        ...n.attrs,
                        src: base64,
                        width,
                        'data-image-id': nodeId
                      });
                      changed = true;
                    }
                  });

                  if (changed) {
                    view.dispatch(tr);
                  }
                } catch (e) {
                  if (!this.options.notifyImageInsertionError) return;
                  this.options.notifyImageInsertionError();

                  processedNodes.add(nodeId);
                  pendingNodes.delete(nodeId);

                  const tr = view.state.tr;
                  let changed = false;

                  view.state.doc.descendants((n, p) => {
                    if (n.type.name === 'customImage' && n.attrs.src === src) {
                      tr.delete(p, p + n.nodeSize);
                      changed = true;
                    }
                  });

                  if (changed) {
                    tr.setMeta('addToHistory', false);
                    view.dispatch(tr);
                  }
                }
              }, 0);
            }

            return true;
          });

          return null;
        }
      })
    ];
  }
});

function getRandomId() {
  return Math.floor(Math.random() * 10 ** 10);
}

function loadImage(src: string | File): Promise<{ base64: string; width: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const loadAndProcess = () => {
      let { width, height } = img;

      if (width > 500) {
        const ratio = 500 / width;
        width = 500;
        height *= ratio;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Null canvas context');

      ctx.drawImage(img, 0, 0, width, height);
      const base64 = canvas.toDataURL('image/png');
      resolve({ base64, width });
    };

    img.onload = loadAndProcess;
    img.onerror = () => reject(new Error('Failed to upload image.'));

    if (src instanceof File) {
      const reader = new FileReader();
      reader.onload = e => (img.src = e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(src);
    } else {
      img.src = src;
    }
  });
}
